import type { ReactNode } from "react";
import clsx from "clsx";
import { StatusDot } from "@/components/ui/status-dot";

const DEFAULT_TITLE = "Service disruption";

const AUTH_DEFAULT_MESSAGE =
  "Sign-in may fail due to an outage at a third-party authentication provider. We're monitoring and will restore access as soon as it's resolved.";

const PLATFORM_DEFAULT_MESSAGE =
  "Some features may be unavailable while we resolve an incident. We're working to restore normal service.";

const CARD_SHELL_BASE_CLASS = clsx(
  "rounded-xl border border-amber-200/80 bg-amber-50",
  "shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_1px_3px_rgba(15,23,42,0.06)]",
  "dark:border-transparent dark:bg-slate-800/70",
  "dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.03),0_2px_8px_rgba(0,0,0,0.12)]",
);

const CARD_SHELL_CLASS = clsx(CARD_SHELL_BASE_CLASS, "p-4");

export const OUTAGE_BANNER_INLINE_LINK_CLASS =
  "text-amber-800 underline decoration-amber-400/70 underline-offset-2 transition-colors hover:text-amber-950 dark:text-slate-300 dark:decoration-slate-500 dark:hover:text-slate-100";

export type OutageBannerVariant = "card" | "bar";

export interface OutageBannerProps {
  variant?: OutageBannerVariant;
  title?: string;
  message?: ReactNode;
  statusUrl?: string;
  className?: string;
  "aria-busy"?: boolean;
  "data-testid"?: string;
}

function StatusLink({ statusUrl }: { statusUrl: string }) {
  return (
    <>
      {" "}
      <a
        href={statusUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={OUTAGE_BANNER_INLINE_LINK_CLASS}
        data-testid="outage-banner-status-link"
      >
        Status page
      </a>
    </>
  );
}

function TitlePill({ title }: { title: string }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-amber-500/15 px-3 py-1.5 text-xs font-medium text-amber-800 dark:bg-amber-500/10 dark:text-amber-400">
      <StatusDot color="amber" size="sm" ping />
      {title}
    </span>
  );
}

/**
 * Platform outage / incident banner.
 * Copy is resolved server-side (see `outageBanner.server.ts`) and passed via route loaders.
 */
export function OutageBanner({
  variant = "card",
  title = DEFAULT_TITLE,
  message,
  statusUrl,
  className,
  "aria-busy": ariaBusy,
  "data-testid": dataTestId = "outage-banner",
}: OutageBannerProps) {
  const resolvedMessage =
    message ?? (variant === "bar" ? PLATFORM_DEFAULT_MESSAGE : AUTH_DEFAULT_MESSAGE);

  if (variant === "bar") {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy={ariaBusy}
        data-testid={dataTestId}
        className={clsx(
          CARD_SHELL_BASE_CLASS,
          "w-full max-w-none shrink-0 px-4 py-2",
          className,
        )}
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <TitlePill title={title} />
          <p className="min-w-0 text-[13px] leading-relaxed text-slate-700 dark:text-slate-400">
            {resolvedMessage}
            {statusUrl ? <StatusLink statusUrl={statusUrl} /> : null}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy={ariaBusy}
      data-testid={dataTestId}
      className={clsx(
        CARD_SHELL_CLASS,
        "max-w-lg",
        className,
      )}
    >
      <TitlePill title={title} />
      <p className="mt-2.5 text-[13px] leading-relaxed text-slate-700 dark:text-slate-400">
        {resolvedMessage}
        {statusUrl ? <StatusLink statusUrl={statusUrl} /> : null}
      </p>
    </div>
  );
}
